/**
 * Avisos de consumo de la bolsa mensual del producto activo — OPUS ai u OPUS ci (75 / 90 / 100).
 *
 * El nivel se calcula con `>=` sobre used/quota (bolsa del mes, no kits).
 * 100 = agotada. Sin cuota usable (≤ 0) no hay aviso.
 */

export type TokenAlertLevel = 0 | 75 | 90 | 100;

export type TokenAlertApprox = {
  concepts: number;
  apus: number;
  chats: number;
};

import { etiquetaCreditos } from './creditos.js';
import { nombreProductoActivo } from './brandingDelProducto.js';

const fmtN = new Intl.NumberFormat('es-MX');

export type UnidadAviso = 'tokens' | 'creditos';

/** 0 = sin aviso; 75 / 90 / 100 = umbral alcanzado (`>=`). */
export function tokenAlertLevel(used: number, quota: number): TokenAlertLevel {
  if (!Number.isFinite(used) || !Number.isFinite(quota) || quota <= 0) return 0;
  if (used >= quota) return 100;
  const pct = (used / quota) * 100;
  if (pct >= 90) return 90;
  if (pct >= 75) return 75;
  return 0;
}

function equivalenceLine(approx: TokenAlertApprox): string {
  return (
    `te alcanza para ≈ ${fmtN.format(approx.concepts)} conceptos o ` +
    `${fmtN.format(approx.apus)} APUs o ${fmtN.format(approx.chats)} chats`
  );
}

/** Título + cuerpo en español. El nombre sale del producto activo ("OPUS ai" / "OPUS ci", en minúsculas). */
export function tokenAlertCopy(
  level: TokenAlertLevel,
  remaining: number,
  approx: TokenAlertApprox,
  unidad: UnidadAviso = 'tokens',
): { title: string; body: string } {
  const enCr = unidad === 'creditos';
  const left = enCr ? etiquetaCreditos(Math.max(0, remaining)) : `${fmtN.format(Math.max(0, remaining))} tokens`;
  const eq = equivalenceLine(approx) + (enCr ? ' (promedios)' : '');
  const producto = nombreProductoActivo();
  if (level === 75) {
    return {
      title: `Ya usaste el 75% de la bolsa mensual de ${producto}`,
      body: `Te quedan ${left}: ${eq}. Recarga si no quieres quedarte corto este mes.`,
    };
  }
  if (level === 90) {
    return {
      title: `Ya usaste el 90% de la bolsa mensual de ${producto}`,
      body: `Te quedan ${left}: ${eq}. Recarga ahora para no frenar el trabajo.`,
    };
  }
  if (level === 100) {
    return {
      title: `Se te acabó la bolsa mensual de ${producto}`,
      body: enCr
        ? `Ya no quedan créditos de la bolsa de este mes: ${eq}. Compra una recarga para seguir cotizando (no vence y se usa después de la bolsa).`
        : `Ya no quedan tokens de la bolsa de este mes: ${eq}. Compra una recarga para seguir cotizando (se usan después de la bolsa).`,
    };
  }
  return { title: '', body: '' };
}

/** Identidad para localStorage: correo, si no id, si no plan+tenant. */
export function tokenAlertUserKey(input: {
  email?: string | null;
  userId?: string | null;
  plan?: string | null;
  tenantId?: string | null;
}): string {
  const email = input.email?.trim();
  if (email) return email;
  const id = input.userId?.trim();
  if (id) return id;
  const plan = input.plan?.trim() || 'plan';
  const tenant = input.tenantId?.trim() || 'tenant';
  return `${plan}+${tenant}`;
}

/** Periodo YYYY-MM: `usage.period`, si no month_start, si no hoy. */
export function tokenAlertPeriod(usage: {
  period?: string | null;
  tokens?: { month_start?: string | null };
} | null | undefined): string {
  const p = usage?.period?.trim();
  if (p && /^\d{4}-\d{2}/.test(p)) return p.slice(0, 7);
  const start = usage?.tokens?.month_start;
  if (start) {
    const d = new Date(start);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    }
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function tokenAlertStorageKey(
  userKey: string,
  yearMonth: string,
  level: 75 | 90 | 100,
): string {
  return `opusai.tokenAlert.${userKey}.${yearMonth}.${level}`;
}
